import { motion } from "framer-motion"
import { ArrowRight, PlayCircle, CheckCircle2 } from "lucide-react"
import { useNavigate } from "react-router-dom"

export function Hero() {
  const navigate = useNavigate()

  const handleGetStarted = () => {
    navigate('/auth/register')
  }

  const handleSeeHowItWorks = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-purple-500/10 blur-[100px] animate-pulse" />
        <div
          className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] rounded-full bg-blue-500/10 blur-[100px] animate-pulse"
          style={{ animationDelay: "2s" }}
        />
        <div className="absolute top-[20%] left-[10%] w-24 h-24 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 opacity-20 blur-xl" />
        <div
          className="absolute bottom-[30%] right-[10%] w-32 h-32 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 opacity-20 blur-xl"
          style={{ animationDelay: "1.5s" }}
        />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="flex flex-col items-center text-center max-w-5xl mx-auto">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/50 backdrop-blur-sm border border-white/50 shadow-sm mb-8"
          >
            <span className="flex h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
            <span className="text-sm font-medium text-gray-700">The Future of Apps is Here</span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-5xl md:text-7xl font-black tracking-tight text-gray-900 mb-6 leading-[1.1]"
          >
            Everything you need, <br />
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              all in one place.
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-xl md:text-2xl text-gray-600 mb-10 max-w-2xl leading-relaxed"
          >
            Stop app switching. Start living easier. Z combines 13 powerful modes into one seamless platform for your
            entire digital life.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center gap-4 mb-16"
          >
            <button
              onClick={handleGetStarted}
              className="h-14 px-8 text-lg rounded-full bg-blue-500 hover:bg-blue-600 text-white shadow-xl shadow-blue-500/25 hover:shadow-blue-500/40 transition-all hover:-translate-y-1 font-semibold flex items-center gap-2"
            >
              Get Started Free <ArrowRight className="h-5 w-5" />
            </button>
            <button
              onClick={handleSeeHowItWorks}
              className="h-14 px-8 text-lg rounded-full bg-white/50 backdrop-blur-sm border border-white/60 hover:bg-white/80 transition-all hover:-translate-y-1 font-semibold flex items-center gap-2"
            >
              <PlayCircle className="h-5 w-5 text-blue-500" /> See How It Works
            </button>
          </motion.div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="flex items-center gap-6 text-sm font-medium text-gray-600 mb-20"
          >
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-blue-500" /> 13 Modes
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-blue-500" /> 1 Platform
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-blue-500" /> 100% Free
            </div>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, type: "spring" }}
            className="relative w-full max-w-5xl mx-auto"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/20 bg-white/50 backdrop-blur-xl p-2 md:p-4">
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/10 to-purple-500/10 pointer-events-none" />
              <img
                src="/images/z-app-interface.png"
                alt="Z App Interface"
                className="rounded-xl w-full h-auto shadow-inner"
              />

              {/* Floating Elements */}
              <motion.div
                animate={{ y: [-10, 10, -10] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -right-4 md:-right-12 top-1/4 bg-white p-4 rounded-2xl shadow-xl border border-gray-100 max-w-[200px] hidden md:block"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                    🚗
                  </div>
                  <div>
                    <div className="text-xs font-bold">Ride Arriving</div>
                    <div className="text-[10px] text-gray-500">2 mins away</div>
                  </div>
                </div>
                <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full w-3/4 bg-green-500 rounded-full" />
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [10, -10, 10] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute -left-4 md:-left-12 bottom-1/4 bg-white p-4 rounded-2xl shadow-xl border border-gray-100 max-w-[200px] hidden md:block"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                    💼
                  </div>
                  <div>
                    <div className="text-xs font-bold">New Job Match</div>
                    <div className="text-[10px] text-gray-500">Senior Designer @ Tech</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
